/**
 * MET-05 — ellipsometry, reflectometry, and statistical process control.
 *
 * This module is different from the others in kind. Every other tool CHANGES
 * the wafer; this one only looks at it, and the whole competency is knowing how
 * much to trust what you see.
 *
 * Two ideas carry it.
 *
 * A MEASUREMENT IS A MODEL. Nothing here measures thickness. It measures
 * reflected intensity against wavelength and then fits a film stack to it. If
 * the assumed stack is wrong - wrong refractive index, an interface layer
 * nobody accounted for - the fit still converges, still reports a number to
 * three decimals, and is still wrong. A precise measurement and a correct one
 * are different things.
 *
 * SIGNAL IS NOT THE SAME AS DRIFT. Every reading is the true value plus
 * measurement noise. A control chart that reacts to noise costs more than one
 * that misses a small drift, and knowing which you are looking at is the
 * highest-value decision a shift makes.
 */

import { mean, stdev } from '../../core/math'

/** Refractive index of a few relevant films at visible wavelengths. */
export const INDEX = {
  air: 1.0,
  sio2: 1.462,
  si3n4: 2.02,
  polysilicon: 3.9,
  silicon: 3.88,
} as const

export type FilmMaterial = keyof typeof INDEX

/**
 * Reflectance of a single film on a substrate, from the Airy formula.
 *
 * r = (r01 + r12 e^{-2i beta}) / (1 + r01 r12 e^{-2i beta})
 *
 * The fringes this produces ARE the measurement: their spacing carries the
 * optical thickness n*d, which is why a reflectometer cannot separate a film
 * that is 2% thicker from one whose index is 2% higher without more information.
 */
export function reflectance(
  thicknessNm: number,
  lambdaNm: number,
  filmIndex: number,
  substrateIndex = INDEX.silicon,
  ambientIndex = INDEX.air,
): number {
  const r01 = (ambientIndex - filmIndex) / (ambientIndex + filmIndex)
  const r12 = (filmIndex - substrateIndex) / (filmIndex + substrateIndex)
  const beta = (2 * Math.PI * filmIndex * thicknessNm) / lambdaNm

  // Complex arithmetic written out: numerator and denominator separately.
  const cos2b = Math.cos(2 * beta)
  const sin2b = Math.sin(2 * beta)
  const numRe = r01 + r12 * cos2b
  const numIm = -r12 * sin2b
  const denRe = 1 + r01 * r12 * cos2b
  const denIm = -r01 * r12 * sin2b

  const denMag2 = denRe * denRe + denIm * denIm
  const re = (numRe * denRe + numIm * denIm) / denMag2
  const im = (numIm * denRe - numRe * denIm) / denMag2
  return re * re + im * im
}

export interface Spectrum {
  lambdaNm: number[]
  reflectance: number[]
}

/** Measure a reflectance spectrum, with detector noise. */
export function measureSpectrum(
  thicknessNm: number,
  filmIndex: number,
  noise = 0.004,
  rng: () => number = Math.random,
  lambdaLo = 400,
  lambdaHi = 800,
  points = 81,
): Spectrum {
  const lambdaNm: number[] = []
  const r: number[] = []
  for (let i = 0; i < points; i++) {
    const l = lambdaLo + ((lambdaHi - lambdaLo) * i) / (points - 1)
    lambdaNm.push(l)
    // Box-Muller for gaussian detector noise.
    const u1 = Math.max(rng(), 1e-12)
    const u2 = rng()
    const g = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
    r.push(reflectance(thicknessNm, l, filmIndex) + g * noise)
  }
  return { lambdaNm, reflectance: r }
}

export interface FitResult {
  /** Thickness the fit converged on [nm]. */
  thicknessNm: number
  /** Root-mean-square residual between measurement and model. */
  residual: number
  /** Index the fit assumed - not necessarily the one the film has. */
  assumedIndex: number
}

/**
 * Fit a thickness to a measured spectrum, given an ASSUMED film index.
 *
 * Coarse scan then refine. The assumed index is a parameter rather than a
 * constant on purpose: handing this function the wrong index is how a student
 * discovers that the residual barely moves while the answer moves a lot.
 */
export function fitThickness(
  spectrum: Spectrum,
  assumedIndex: number,
  loNm = 5,
  hiNm = 600,
): FitResult {
  const rms = (t: number) => {
    let acc = 0
    for (let i = 0; i < spectrum.lambdaNm.length; i++) {
      const d = reflectance(t, spectrum.lambdaNm[i], assumedIndex) - spectrum.reflectance[i]
      acc += d * d
    }
    return Math.sqrt(acc / spectrum.lambdaNm.length)
  }

  let best = loNm
  let bestErr = Infinity
  for (let t = loNm; t <= hiNm; t += 0.5) {
    const e = rms(t)
    if (e < bestErr) {
      bestErr = e
      best = t
    }
  }
  // Refine around the coarse minimum.
  for (let t = Math.max(best - 0.5, loNm); t <= best + 0.5; t += 0.02) {
    const e = rms(t)
    if (e < bestErr) {
      bestErr = e
      best = t
    }
  }
  return { thicknessNm: best, residual: bestErr, assumedIndex }
}

// ------------------------------------------------------- gauge capability ---

export interface GaugeStudy {
  /** Repeated measurements of one unchanging wafer. */
  readings: number[]
  /** Measurement repeatability, 1 sigma [nm]. */
  repeatability: number
  /** Process variation the gauge has to resolve, 1 sigma [nm]. */
  processSigma: number
  /**
   * Gauge repeatability and reproducibility as a percentage of total variation.
   * Under 10% is capable; over 30% means the gauge cannot see the process.
   */
  grrPct: number
  capable: boolean
}

/**
 * Measure the same wafer repeatedly and compare the spread against the spread
 * the process itself produces.
 *
 * A gauge is not judged in absolute terms. A tool with 1 nm repeatability is
 * excellent for a 500 nm film and useless for a 2 nm one, and the number that
 * decides is the ratio.
 */
export function gaugeStudy(
  trueThicknessNm: number,
  filmIndex: number,
  detectorNoise: number,
  processSigmaNm: number,
  repeats = 25,
  rng: () => number = Math.random,
): GaugeStudy {
  const readings: number[] = []
  for (let i = 0; i < repeats; i++) {
    const s = measureSpectrum(trueThicknessNm, filmIndex, detectorNoise, rng)
    readings.push(fitThickness(s, filmIndex).thicknessNm)
  }
  const repeatability = stdev(readings)
  const total = Math.sqrt(repeatability * repeatability + processSigmaNm * processSigmaNm)
  const grrPct = total > 0 ? (100 * repeatability) / total : 100
  return {
    readings,
    repeatability,
    processSigma: processSigmaNm,
    grrPct,
    capable: grrPct < 30,
  }
}

// ---------------------------------------------------------------------- SPC ---

export interface ControlChart {
  /** Measured value for each lot. */
  values: number[]
  /** The true process value, which a technician never actually sees. */
  truth: number[]
  centre: number
  upperControl: number
  lowerControl: number
  upperSpec: number
  lowerSpec: number
  /** Indices flagged by the Western Electric rules, with the rule that fired. */
  violations: Array<{ index: number; rule: string }>
  /** Process capability against the spec limits. */
  cpk: number
}

/**
 * The Western Electric rules, on any series.
 *
 * Lifted out of the chart generator so it is not tied to lots on one tool.
 * The same rules apply to a series of measurements taken at one tool over a
 * shift and to a series taken at the SAME tool on eleven different layers of
 * one product - and the second case is the one that catches a drift no single
 * measurement can, because every point is inside the limits and the pattern is
 * not.
 *
 * Rules 1, 2 and 3 are the ones a shift actually uses. Rule 3 is the
 * important one here: eight consecutive points on one side of the centreline
 * has a chance of about 1 in 128 if the process is centred, so seeing it means
 * the process is not centred - even though nothing is out of specification.
 */
export function westernElectricViolations(
  values: number[],
  centre: number,
  sigma: number,
): Array<{ index: number; rule: string }> {
  const violations: Array<{ index: number; rule: string }> = []
  const upperControl = centre + 3 * sigma
  const lowerControl = centre - 3 * sigma
  for (let i = 0; i < values.length; i++) {
    if (values[i] > upperControl || values[i] < lowerControl) {
      violations.push({ index: i, rule: '1 point beyond 3σ' })
      continue
    }
    if (i >= 2) {
      const w = values.slice(i - 2, i + 1)
      const above = w.filter((v) => v > centre + 2 * sigma).length
      const below = w.filter((v) => v < centre - 2 * sigma).length
      if (above >= 2 || below >= 2) {
        violations.push({ index: i, rule: '2 of 3 beyond 2σ' })
        continue
      }
    }
    if (i >= 7) {
      const w = values.slice(i - 7, i + 1)
      if (w.every((v) => v > centre) || w.every((v) => v < centre)) {
        violations.push({ index: i, rule: '8 in a row on one side' })
        continue
      }
    }
    // Six in a row all climbing or all falling is a trend, and a trend on a
    // tool that is revisited once per layer is exactly what a slow drift looks
    // like from the route.
    if (i >= 5) {
      const w = values.slice(i - 5, i + 1)
      let rising = true
      let falling = true
      for (let k = 1; k < w.length; k++) {
        if (w[k] <= w[k - 1]) rising = false
        if (w[k] >= w[k - 1]) falling = false
      }
      if (rising || falling) {
        violations.push({ index: i, rule: '6 in a row trending' })
      }
    }
  }
  return violations
}

/**
 * Generate a run of lots and chart them.
 *
 * The chart holds BOTH the measured series and the true one. A student only
 * ever sees the measured series; having the truth available is what lets the
 * module show, afterwards, whether an alarm was real.
 */
export function runControlChart(opts: {
  lots: number
  target: number
  processSigma: number
  measurementSigma: number
  /** Drift per lot, in nm. Zero for a stable process. */
  driftPerLot?: number
  /** Lot at which a step change happens, if any. */
  stepAtLot?: number
  stepSize?: number
  specHalfWidth: number
  rng?: () => number
}): ControlChart {
  const rng = opts.rng ?? Math.random
  const normal = () => {
    const u1 = Math.max(rng(), 1e-12)
    const u2 = rng()
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
  }

  const truth: number[] = []
  const values: number[] = []
  for (let i = 0; i < opts.lots; i++) {
    let t = opts.target + (opts.driftPerLot ?? 0) * i
    if (opts.stepAtLot !== undefined && i >= opts.stepAtLot) t += opts.stepSize ?? 0
    t += normal() * opts.processSigma
    truth.push(t)
    values.push(t + normal() * opts.measurementSigma)
  }

  // Control limits from the first stretch, as a real chart is set up.
  const baselineCount = Math.min(20, values.length)
  const baseline = values.slice(0, baselineCount)
  const centre = mean(baseline)
  const sigma = stdev(baseline)
  const upperControl = centre + 3 * sigma
  const lowerControl = centre - 3 * sigma

  const violations = westernElectricViolations(values, centre, sigma)

  const upperSpec = opts.target + opts.specHalfWidth
  const lowerSpec = opts.target - opts.specHalfWidth
  const total = stdev(values)
  const cpk =
    total > 0 ? Math.min(upperSpec - mean(values), mean(values) - lowerSpec) / (3 * total) : 0

  return { values, truth, centre, upperControl, lowerControl, upperSpec, lowerSpec, violations, cpk }
}

/** A small deterministic generator, so a lesson repeats identically. */
export function seededRng(seed = 20260827): () => number {
  let s = seed
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff
    return s / 0x7fffffff
  }
}
